import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin } from "lucide-react";
import GroupHeader from "@/components/GroupHeader";
import GroupFooter from "@/components/GroupFooter";
import SEO from "@/components/SEO";
import InquiryForm from "@/components/site/InquiryForm";
import { Eyebrow, PillButton, Reveal, StatusBadge, Tbc } from "@/components/site/Bits";
import { api } from "@/lib/api";

function Spec({ label, value }) {
    return (
        <div className="py-5 border-t border-hairline grid grid-cols-2 gap-4">
            <dt className="mono-label text-muted_text">{label}</dt>
            <dd className="text-forest" data-testid={`spec-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value ? value : <Tbc />}</dd>
        </div>
    );
}

function Gallery({ items }) {
    if (!items?.length) return null;
    return (
        <div className="grid sm:grid-cols-2 gap-6" data-testid="project-gallery">
            {items.map((g, i) => (
                <Reveal key={g.url + i} delay={i * 80} className={`relative overflow-hidden rounded-2xl img-zoom ${i === 0 && items.length % 2 ? "sm:col-span-2 aspect-[16/9]" : "aspect-[4/3]"}`}>
                    {g.kind === "video"
                        ? <video src={g.url} controls muted playsInline className="h-full w-full object-cover" />
                        : <img src={g.url} alt={g.caption || "Project image"} loading="lazy" className="h-full w-full object-cover" />}
                    <div className="absolute left-4 bottom-4 flex flex-wrap gap-2">
                        {g.caption && <span className="rounded-full bg-cream/90 px-3 py-1.5 text-xs text-forest">{g.caption}</span>}
                    </div>
                </Reveal>
            ))}
        </div>
    );
}

export default function ProjectDetail() {
    const { slug } = useParams();
    const [p, setP] = useState(undefined);
    useEffect(() => {
        api.get(`/projects/${slug}`).then(({ data }) => setP(data)).catch(() => setP(null));
    }, [slug]);

    if (p === null) {
        return (
            <div className="bg-cream min-h-screen" data-testid="project-not-found">
                <GroupHeader alwaysDark />
                <div className="max-w-3xl mx-auto px-6 pt-48 pb-32">
                    <h1 className="font-serif text-5xl text-forest mb-6">Project not found.</h1>
                    <PillButton to="/projects">See all projects</PillButton>
                </div>
                <GroupFooter />
            </div>
        );
    }
    if (!p) return <div className="bg-forest min-h-screen"><GroupHeader alwaysDark /></div>;

    const ld = {
        "@context": "https://schema.org", "@type": "Place", name: p.title, description: p.summary,
        address: { "@type": "PostalAddress", addressLocality: p.location, addressRegion: p.region, addressCountry: "PT" },
        url: `https://verde2050.com/projects/${p.slug}`,
    };

    return (
        <div data-testid="project-detail-page" className="bg-cream text-ink min-h-screen">
            <SEO path={`/projects/${p.slug}`} og={p.slug === "sintra-residential-development" ? "sintra" : undefined} image={p.hero_image} title={p.seo_title || p.title} description={p.seo_description || p.summary} jsonLd={ld} />
            <GroupHeader />
            <section className="relative isolate min-h-[92svh] flex items-end overflow-hidden bg-forest text-cream pb-16 pt-32">
                <img src={p.hero_image} alt={`${p.title}, ${p.location}`} className="absolute inset-0 -z-10 h-full w-full object-cover hero-zoom" loading="eager" />
                <div className="absolute inset-0 -z-10 bg-gradient-to-b from-forest/40 via-forest/20 to-forest/95" />
                <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
                    <Link to="/projects" data-testid="project-back" className="inline-flex items-center gap-2 mono-label text-cream/70 hover:text-cream mb-10"><ArrowLeft className="h-4 w-4" /> All projects</Link>
                    <div className="flex flex-wrap gap-2 mb-8"><StatusBadge status={p.status} dark /></div>
                    <h1 data-testid="project-headline" className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight max-w-5xl">{p.headline || p.title}</h1>
                    <p className="mt-6 inline-flex items-center gap-2 text-cream/80"><MapPin className="h-4 w-4" /> {p.location}{p.region ? ` · ${p.region}` : ""}</p>
                </div>
            </section>

            <section className="py-20 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14">
                    <div className="lg:col-span-7">
                        <Reveal><Eyebrow>Project overview</Eyebrow></Reveal>
                        <Reveal delay={100}><p className="mt-8 text-xl md:text-2xl text-ink leading-relaxed font-light">{p.summary}</p></Reveal>
                        <Reveal delay={200} className="mt-8 space-y-5 text-base md:text-lg text-muted_text leading-relaxed">
                            {(p.description || "").split("\n").filter(Boolean).map((para, i) => <p key={i}>{para}</p>)}
                        </Reveal>
                        {p.highlights?.length > 0 && (
                            <Reveal delay={220} className="mt-12">
                                <p className="mono-label text-moss mb-5">Highlights</p>
                                <ul className="grid sm:grid-cols-2 gap-px bg-hairline rounded-xl overflow-hidden" data-testid="project-highlights">
                                    {p.highlights.map((h, i) => (
                                        <li key={h} className="flex gap-4 bg-cream p-5 text-sm text-forest leading-relaxed">
                                            <span className="num-mono text-moss pt-0.5">{String(i + 1).padStart(2, "0")}</span>{h}
                                        </li>
                                    ))}
                                </ul>
                            </Reveal>
                        )}
                        <Reveal delay={250} className="mt-10"><PillButton to="#request-information" testid="project-request-im-top" onClick={(e) => { e.preventDefault(); document.getElementById("request-information")?.scrollIntoView({ behavior: "smooth" }); }}>Request Project Information</PillButton></Reveal>
                    </div>
                    <Reveal delay={150} className="lg:col-span-5">
                        <dl className="rounded-2xl bg-surface p-8 md:p-10 border-b border-hairline" data-testid="project-specs">
                            <p className="mono-label text-moss mb-6">Key facts</p>
                            <Spec label="Location" value={p.location} />
                            <Spec label="Type" value={p.type} />
                            <Spec label="Programme" value={p.program} />
                            <Spec label="Status" value={p.status} />
                            <Spec label="Licensing status" value={p.licensing_status} />
                            <Spec label="Site area" value={p.site_area} />
                            <Spec label="Construction area" value={p.permitted_area} />
                        </dl>
                        <p className="mt-4 text-xs text-muted_text leading-relaxed">All figures are indicative and subject to confirmation in due diligence. Further planning parameters are shared under NDA.</p>
                    </Reveal>
                </div>
            </section>

            <section className="pb-20 md:pb-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Gallery items={p.gallery} />
                    {p.video_url && (
                        <div className="mt-6 overflow-hidden rounded-2xl aspect-video bg-forest" data-testid="project-video">
                            <video src={p.video_url} controls muted playsInline className="h-full w-full object-cover" />
                        </div>
                    )}
                </div>
            </section>

            {p.map_query && (
                <section className="pb-20 md:pb-32" data-testid="project-map">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <Reveal><Eyebrow>Location</Eyebrow></Reveal>
                        <Reveal delay={100} className="mt-8 overflow-hidden rounded-2xl aspect-[16/9] md:aspect-[21/9] bg-surface">
                            <iframe title={`Map of ${p.location}`} src={`https://www.google.com/maps?q=${encodeURIComponent(p.map_query)}&z=12&output=embed`}
                                    className="h-full w-full border-0 grayscale-[0.4]" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                        </Reveal>
                        {p.distances?.length > 0 && (
                            <dl className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-hairline rounded-xl overflow-hidden" data-testid="project-distances">
                                {p.distances.map((d) => (
                                    <div key={d.label} className="bg-cream p-5">
                                        <dd className="font-serif text-3xl text-forest">{d.value}</dd>
                                        <dt className="text-xs text-muted_text mt-1">{d.label}</dt>
                                    </div>
                                ))}
                            </dl>
                        )}
                        <p className="mt-3 text-xs text-muted_text">Indicative area and approximate drive times. The exact site location is shared with qualified parties.</p>
                    </div>
                </section>
            )}

            <section id="request-information" data-testid="project-request-information" className="bg-surface py-24 md:py-32 scroll-mt-20">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14">
                    <div className="lg:col-span-5">
                        <Reveal><Eyebrow>Information Memorandum</Eyebrow></Reveal>
                        <Reveal delay={100}><h2 className="font-serif text-4xl sm:text-5xl text-forest leading-[1.04] tracking-tight mt-8">Request project <em className="italic">information.</em></h2></Reveal>
                        <Reveal delay={200}><p className="mt-8 text-base md:text-lg text-muted_text leading-relaxed">For developers and investors. Tell us a little about yourself and we'll share the Information Memorandum, plans and planning documentation.</p></Reveal>
                    </div>
                    <Reveal delay={150} className="lg:col-span-7">
                        <InquiryForm lockType defaultType="Request Information Memorandum" projectSlug={p.slug} submitLabel="Request Information Memorandum"
                                     messagePlaceholder="Who you are, what you build or invest in, and what you'd like to know about this project." />
                    </Reveal>
                </div>
            </section>
            <GroupFooter />
        </div>
    );
}
